// src/components/admin/AssimilationTabContent.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { FiFileText, FiUploadCloud, FiEye, FiPaperclip, FiX, FiSave, FiLoader, FiCheck, FiEdit, FiPlus, FiDownload, FiTrash2, FiArrowLeft, FiList, FiClock, FiCheckCircle } from 'react-icons/fi';

const FormSection = ({ title, description, children, icon }) => (
  <div className="p-4 sm:p-6 bg-white rounded-xl shadow-sm border border-slate-200 space-y-3">
    <h3 className="text-lg font-semibold text-slate-800 flex items-center">
      {icon || <FiEdit className="h-5 w-5 mr-3 text-blue-500" />}
      {title}
    </h3>
    {description && <p className="text-sm text-slate-500">{description}</p>}
    <div className="pt-2">{children}</div>
  </div>
);

// Composant pour gérer l'upload de fichiers par PJ
const PJUploadSection = ({ pjId, files, onFileChange, onFileRemove, isChecked }) => {
  if (!isChecked) return null;
  return (
    <div className="ml-8 mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
      <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center mb-2">
        <label htmlFor={`upload-${pjId}`} className="cursor-pointer">
          <FiUploadCloud className="mx-auto h-6 w-6 text-slate-400" />
          <span className="mt-1 block text-xs font-medium text-blue-600 hover:text-blue-500">Ajouter des fichiers</span>
          <input id={`upload-${pjId}`} type="file" className="sr-only" multiple onChange={(e) => onFileChange(pjId, e.target.files)} />
        </label>
      </div>
      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
              <div className="flex items-center truncate">
                <FiPaperclip className="h-4 w-4 text-slate-500 flex-shrink-0" />
                <span className="ml-2 text-xs text-slate-700 truncate" title={file.name}>{file.name}</span>
                <span className="ml-2 text-xs text-slate-500">
                  ({Math.round(file.size / 1024)} KB)
                </span>
              </div>
              <button onClick={() => onFileRemove(pjId, index)} className="text-red-500 hover:text-red-700 ml-2">
                <FiX className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Composant Dashboard
const AssimilationDashboard = ({ courriers, onCreateNew, onViewCourrier, onEditCourrier, onDeleteCourrier, onDownloadCourrier, clientData }) => {
  const statsData = {
    total: courriers.length,
    brouillon: courriers.filter(c => c.status === 'draft').length,
    termine: courriers.filter(c => c.status === 'completed').length,
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec call-to-action */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-6">
        <h2 className="text-2xl font-bold mb-4">Courriers d'Assimilation</h2>
        <p className="text-blue-100 mb-6">
          Créez et gérez vos demandes d'assimilation d'activité industrielle pour la réduction/exonération de CSPE
        </p>
        <button
          onClick={onCreateNew}
          className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center"
        >
          <FiPlus className="h-5 w-5 mr-2" />
          Créer votre nouveau courrier d'assimilation
        </button>
      </div>

      {/* Informations Client */}
      {clientData && (
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Informations Client</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="bg-white p-3 rounded border">
              <strong className="text-slate-700">Entreprise :</strong> 
              <span className="ml-2 text-slate-900">{clientData.nom_entreprise}</span>
            </div>
            <div className="bg-white p-3 rounded border">
              <strong className="text-slate-700">SIRET :</strong> 
              <span className="ml-2 text-slate-900">{clientData.siret}</span>
            </div>
            <div className="bg-white p-3 rounded border">
              <strong className="text-slate-700">Adresse :</strong> 
              <span className="ml-2 text-slate-900">{clientData.adresse_siege}</span>
            </div>
            <div className="bg-white p-3 rounded border">
              <strong className="text-slate-700">Dirigeant :</strong> 
              <span className="ml-2 text-slate-900">{clientData.dirigeant_nom}</span>
            </div>
          </div>
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-slate-200">
          <div className="flex items-center">
            <FiFileText className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-600">Total</p>
              <p className="text-2xl font-bold text-slate-900">{statsData.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-slate-200">
          <div className="flex items-center">
            <FiEdit className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-600">Brouillon</p>
              <p className="text-2xl font-bold text-slate-900">{statsData.brouillon}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-slate-200">
          <div className="flex items-center">
            <FiCheckCircle className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-600">Terminé</p>
              <p className="text-2xl font-bold text-slate-900">{statsData.termine}</p>
            </div>
          </div>
        </div>
        
      </div>

      {/* Liste des courriers */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center">
            <FiList className="h-5 w-5 mr-2" />
            Mes courriers d'assimilation
          </h3>
        </div>
        <div className="p-6">
          {courriers.length === 0 ? (
            <div className="text-center py-8">
              <FiFileText className="mx-auto h-12 w-12 text-slate-400 mb-4" />
              <p className="text-slate-500">Aucun courrier créé pour le moment</p>
              <button
                onClick={onCreateNew}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer votre premier courrier
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {courriers.map((courrier) => (
                <div key={courrier.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  {/* ✅ CORRECTION ICI pour le responsive */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    {/* Partie 1 : Informations */}
                    <div className="flex-1 min-w-0"> {/* min-w-0 est important pour le truncate */}
                      <div className="flex items-center mb-2 flex-wrap">
                        <h4 className="text-lg font-semibold text-slate-900 mr-3">
                          {courrier.objet_courrier || 'Courrier sans objet'}
                        </h4>
                        <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                          courrier.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                          courrier.status === 'completed' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {courrier.status === 'draft' ? 'Brouillon' :
                           courrier.status === 'completed' ? 'Terminé' : 'Envoyé'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-2">
                        SIE: {courrier.sie_nom || 'Non spécifié'}
                      </p>
                      <p className="text-xs text-slate-500">
                        Créé le {new Date(courrier.created_at).toLocaleDateString('fr-FR')} • 
                        Modifié le {new Date(courrier.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    {/* Partie 2 : Boutons d'action */}
                    <div className="flex-shrink-0 flex items-center space-x-2 self-start sm:self-center">
                      <button onClick={() => onViewCourrier(courrier)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Visualiser">
                        <FiEye className="h-4 w-4" />
                      </button>
                      <button onClick={() => onEditCourrier(courrier)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Modifier">
                        <FiEdit className="h-4 w-4" />
                      </button>
                      <button onClick={() => onDownloadCourrier(courrier)} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="Télécharger PDF">
                        <FiDownload className="h-4 w-4" />
                      </button>
                      <button onClick={() => onDeleteCourrier(courrier)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer">
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant de visualisation du courrier
const CourrierViewer = ({ courrier, onBack, onEdit, onDownload, clientData }) => {
  const generateCourrierContent = (courrier) => {
    const aujourdhui = new Date(courrier.created_at).toLocaleDateString('fr-FR');
    const justificatifs = [];
    
    if (courrier.pieces_jointes) {
      Object.entries(courrier.pieces_jointes).forEach(([pjId, pjData]) => {
        if (pjData.checked) {
          const availablePJ = [
            { id: 'descriptif', label: 'Descriptif précis du procédé de fabrication (Annexe 1)' },
            { id: 'photos', label: 'Photos du matériel et des différentes étapes' },
            { id: 'attestation', label: 'Attestation sur l\'honneur de l\'exploitant' },
            { id: 'factures', label: 'Factures d\'achat de matières premières et de vente' },
            { id: 'kbis', label: 'Extrait Kbis' },
            { id: 'autres', label: 'Autres justificatifs' }
          ];
          const pjInfo = availablePJ.find(pj => pj.id === pjId);
          if (pjInfo) {
            if (pjId === 'autres' && pjData.description) {
              justificatifs.push(pjData.description);
            } else {
              justificatifs.push(pjInfo.label);
            }
          }
        }
      });
    }
    
    return `${clientData?.nom_entreprise || '[Nom de l\'entreprise]'}
${clientData?.adresse_siege || '[Adresse]'}
SIRET : ${clientData?.siret || '[SIRET]'}

À l'attention du ${courrier.sie_nom || '[Nom du SIE]'}
${courrier.sie_adresse || '[Adresse du SIE]'}

${courrier.sie_ville || '[Ville]'}, le ${aujourdhui}

Objet : ${courrier.objet_courrier || '[Objet du courrier]'}
PJ : ${justificatifs.length > 0 ? justificatifs.join(', ') : '[Pièces jointes]'}

Madame, Monsieur,

${courrier.premier_paragraphe || '[Premier paragraphe]'}

I. Activité de l'entreprise
${courrier.activite_entreprise || '[Description de l\'activité]'}

II. Fondement légal et doctrinal
Conformément aux dispositions du BOFiP-Impôts – BOI-TAXE-10-20-40 (§ 70 et suivants), il est précisé que :

"L'éligibilité à la réduction/exonération de CSPE s'apprécie au regard de la réalité de l'activité exercée, indépendamment du code NAF/APE de l'entreprise, dès lors qu'une activité industrielle de transformation est effectivement réalisée et dûment justifiée."

De plus, selon le BOI-TAXE-10-20-40 § 80 :

"Une entreprise dont le code APE n'est pas a priori éligible peut bénéficier du dispositif dès lors que l'activité réellement exercée relève de la transformation de matière, caractéristique des activités industrielles…"

III. Justification de la demande
Nous joignons à la présente :

${justificatifs.map(just => `• ${just}`).join('\n')}

IV. Demande
En conséquence, nous sollicitons l'assimilation de notre activité à une activité industrielle éligible à la réduction/exonération de CSPE, conformément à la doctrine précitée, et ce, nonobstant notre code APE actuel.

Nous restons à votre disposition pour tout complément d'information ou contrôle sur place, et vous prions de bien vouloir nous confirmer la prise en compte de notre demande.

Nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.

${courrier.attestation_honneur || '[Attestation sur l\'honneur]'}

${clientData?.dirigeant_nom || '[Nom du signataire]'}

---

ANNEXE 1 : DESCRIPTIF DU PROCÉDÉ DE FABRICATION

${courrier.procede_fabrication || '[Descriptif détaillé du procédé]'}`;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={onBack}
              className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors mr-3"
            >
              <FiArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-bold text-slate-900">Visualisation du courrier</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(courrier)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <FiEdit className="h-4 w-4 mr-2" />
              Modifier
            </button>
            <button
              onClick={() => onDownload(courrier)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <FiDownload className="h-4 w-4 mr-2" />
              Télécharger PDF
            </button>
          </div>
        </div>

        <div className="bg-slate-50 p-6 rounded-lg border border-slate-200">
          <pre className="whitespace-pre-wrap text-sm font-mono text-slate-800 leading-relaxed">
            {generateCourrierContent(courrier)}
          </pre>
        </div>
      </div>
    </div>
  );
};

const AssimilationTabContent = ({ clientId, client }) => {
  const [view, setView] = useState('dashboard'); // 'dashboard', 'create', 'edit', 'view'
  const [selectedCourrier, setSelectedCourrier] = useState(null);
  const [courriers, setCourriers] = useState([]);
  const [formData, setFormData] = useState({
    // Informations SIE
    sieNom: '',
    sieAdresse: '',
    sieVille: '',
    
    // Contenu du courrier
    objetCourrier: '',
    premierParagraphe: '',
    activiteEntreprise: '',
    attestationHonneur: '',
    
    // Annexe 1
    procededFabrication: '',
    
    // Pièces jointes
    piecesJointes: {
      descriptif: { checked: false, files: [] },
      photos: { checked: false, files: [] },
      attestation: { checked: true, files: [] }, 
      factures: { checked: false, files: [] },
      kbis: { checked: false, files: [] },
      autres: { checked: false, files: [], description: '' }
    }
  });

  const [clientData, setClientData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Configuration des PJ disponibles
  const availablePJ = [
    { id: 'descriptif', label: 'Descriptif précis du procédé de fabrication (Annexe 1)' },
    { id: 'photos', label: 'Photos du matériel et des différentes étapes' },
    { id: 'attestation', label: 'Attestation sur l\'honneur de l\'exploitant' },
    { id: 'factures', label: 'Factures d\'achat de matières premières et de vente' },
    { id: 'kbis', label: 'Extrait Kbis' },
    { id: 'autres', label: 'Autres justificatifs' }
  ];

  // Récupération des données client
  const fetchClientData = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('siren')
        .eq('id', clientId)
        .single();

      if (profileError) {
        throw new Error(`Erreur lors de la récupération du profil: ${profileError.message}`);
      }

      if (!profileData || !profileData.siren) {
        throw new Error(`Aucun SIREN trouvé pour le client ID: ${clientId}`);
      }

      const siren = profileData.siren;

      const { data, error } = await supabase
        .from('pappers_data')
        .select('company_name, address_line1, postal_code, city, siret_siege, naf_code, representant_nom, representant_prenom, representant_qualite')
        .eq('siren', siren)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setClientData({
          nom_entreprise: data.company_name,
          adresse_siege: `${data.address_line1}, ${data.postal_code} ${data.city}`,
          siret: data.siret_siege,
          dirigeant_nom: `${data.representant_prenom || ''} ${data.representant_nom || ''}`.trim(),
          naf_code: data.naf_code,
          representant_qualite: data.representant_qualite
        });

        // Pré-remplissage intelligent uniquement pour nouveau courrier
        if (view === 'create') {
          const companyName = data.company_name || '[Nom de l\'entreprise]';
          const nafCode = data.naf_code || '[Code APE]';
          const dirigeant = `${data.representant_prenom || ''} ${data.representant_nom || '[Nom du Dirigeant]'}`.trim();
          const qualite = data.representant_qualite || '[Qualité du Dirigeant]';

          setFormData(prev => ({
            ...prev,
            objetCourrier: prev.objetCourrier || `Demande d'assimilation de l'activité de ${companyName} à une activité industrielle éligible à la réduction/exonération de CSPE`,
            premierParagraphe: prev.premierParagraphe || `Nous sollicitons, par la présente, l'assimilation de notre activité de fabrication à une activité industrielle au sens des articles 266 quinquies C et suivants du Code des douanes et de l'article 238 du Code général des impôts (CGI), dans le cadre de notre demande de réduction/exonération de Contribution au Service Public de l'Électricité (CSPE).`,
            activiteEntreprise: prev.activiteEntreprise || `Notre entreprise, ${companyName}, actuellement enregistrée sous le code APE ${nafCode}, exerce principalement une activité de transformation de matières premières en produits finis destinés à la vente.`,
            attestationHonneur: prev.attestationHonneur || `Je soussigné(e), ${dirigeant}, agissant en qualité de ${qualite} de la société ${companyName}, atteste sur l'honneur que la fabrication constitue l'activité principale de l'entreprise, représentant plus de 50% du chiffre d'affaires et du temps de travail annuel.`
          }));
        }
      }
    } catch (error) {
      console.error("Erreur récupération données client:", error);
    }
  }, [clientId, view]);

  // Récupération de tous les courriers
  const fetchCourriers = useCallback(async () => {
    if (!clientId) return;
    try {
      const { data, error } = await supabase
        .from('assimilations')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setCourriers(data || []);
    } catch (error) {
      console.error("Erreur récupération courriers:", error);
    }
  }, [clientId]);

  // Charger les données d'un courrier spécifique pour édition
  const loadCourrierForEdit = useCallback((courrier) => {
    setFormData({
      sieNom: courrier.sie_nom || '',
      sieAdresse: courrier.sie_adresse || '',
      sieVille: courrier.sie_ville || '',
      objetCourrier: courrier.objet_courrier || '',
      premierParagraphe: courrier.premier_paragraphe || '',
      activiteEntreprise: courrier.activite_entreprise || '',
      attestationHonneur: courrier.attestation_honneur || '',
      procededFabrication: courrier.procede_fabrication || '',
      piecesJointes: courrier.pieces_jointes || {
        descriptif: { checked: false, files: [] },
        photos: { checked: false, files: [] },
        attestation: { checked: true, files: [] }, 
        factures: { checked: false, files: [] },
        kbis: { checked: false, files: [] },
        autres: { checked: false, files: [], description: '' }
      }
    });
  }, []);

  // Réinitialiser le formulaire
  const resetForm = useCallback(() => {
    setFormData({
      sieNom: '',
      sieAdresse: '',
      sieVille: '',
      objetCourrier: '',
      premierParagraphe: '',
      activiteEntreprise: '',
      attestationHonneur: '',
      procededFabrication: '',
      piecesJointes: {
        descriptif: { checked: false, files: [] },
        photos: { checked: false, files: [] },
        attestation: { checked: true, files: [] }, 
        factures: { checked: false, files: [] },
        kbis: { checked: false, files: [] },
        autres: { checked: false, files: [], description: '' }
      }
    });
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchClientData();
      await fetchCourriers();
      setIsLoading(false);
    };
    if (clientId) {
      loadData();
    } else {
      setIsLoading(false);
      console.warn("Aucun clientId fourni à AssimilationTabContent.");
    }
  }, [clientId, fetchClientData, fetchCourriers]);

  // Handlers pour la navigation
  const handleCreateNew = () => {
    resetForm();
    setSelectedCourrier(null);
    setView('create');
  };

  const handleViewCourrier = (courrier) => {
    setSelectedCourrier(courrier);
    setView('view');
  };

  const handleEditCourrier = (courrier) => {
    setSelectedCourrier(courrier);
    loadCourrierForEdit(courrier);
    setView('edit');
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
    setSelectedCourrier(null);
    fetchCourriers(); // Rafraîchir la liste
  };

  const handleDeleteCourrier = async (courrier) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce courrier ?')) {
      try {
        const { error } = await supabase
          .from('assimilations')
          .delete()
          .eq('id', courrier.id);
        
        if (error) throw error;
        
        await fetchCourriers();
        alert('Courrier supprimé avec succès !');
      } catch (error) {
        console.error('Erreur suppression:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleDownloadCourrier = async (courrier) => {
    setIsGenerating(true);
    try {
      // ÉTAPE 1: Importer les librairies (inchangé)
      const { jsPDF } = await import('jspdf');
      const { PDFDocument } = await import('pdf-lib');

      // ÉTAPE 2: Catégoriser les pièces jointes (inchangé)
      const imageAttachments = [];
      const pdfAttachments = [];
      const otherAttachments = [];

      if (courrier.pieces_jointes) {
        for (const [pjId, pjData] of Object.entries(courrier.pieces_jointes)) {
          if (pjData.checked && pjData.files && pjData.files.length > 0) {
            for (const filePath of pjData.files) {
              if (typeof filePath !== 'string') continue;
              const { data: urlData } = await supabase.storage.from('pieces-jointes-assimilation').createSignedUrl(filePath, 3600);
              if (urlData) {
                const fileExtension = filePath.split('.').pop().toLowerCase();
                const attachmentData = { name: filePath.split('/').pop(), url: urlData.signedUrl, type: pjId };
                if (['jpg', 'jpeg', 'png'].includes(fileExtension)) imageAttachments.push(attachmentData);
                else if (fileExtension === 'pdf') pdfAttachments.push(attachmentData);
                else otherAttachments.push(attachmentData);
              }
            }
          }
        }
      }

      // ✅ ÉTAPE 3: Générer le PDF principal avec une fonction de texte corrigée
      const mainPdfDoc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = mainPdfDoc.internal.pageSize.getWidth();
      const pageHeight = mainPdfDoc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      // --- CORRECTION CLÉ : NOUVELLE FONCTION PLUS INTELLIGENTE ---
      const addTextWithWrap = (text, options = {}) => {
        const { fontSize = 10, isBold = false, x = margin, align = 'left' } = options;
        mainPdfDoc.setFontSize(fontSize);
        mainPdfDoc.setFont(undefined, isBold ? 'bold' : 'normal');

        const lines = mainPdfDoc.splitTextToSize(text, pageWidth - (2 * margin));
        
        // On parcourt chaque ligne et on ajoute un saut de page si nécessaire AVANT d'écrire
        lines.forEach(line => {
          if (yPosition > pageHeight - margin) {
            mainPdfDoc.addPage();
            yPosition = margin; // Réinitialise en haut de la nouvelle page
          }
          mainPdfDoc.text(line, x, yPosition, { align: align });
          yPosition += 6; // On descend d'une hauteur de ligne
        });
        
        yPosition += 2; // Espace après le bloc
      };

      // --- SECTION 1 : Le corps du courrier ---
      const courrierBody = generateCourrierPreview(courrier).split('---')[0];
      
      addTextWithWrap("COURRIER D'ASSIMILATION", { fontSize: 16, isBold: true, x: pageWidth / 2, align: 'center' });
      yPosition += 10; // Espace après le titre
      addTextWithWrap(courrierBody.trim());

      // --- SECTION 2 : Annexe 1 sur une NOUVELLE PAGE ---
      mainPdfDoc.addPage();
      yPosition = margin;
      addTextWithWrap("ANNEXE 1 : DESCRIPTIF DU PROCÉDÉ DE FABRICATION", { fontSize: 12, isBold: true });
      yPosition += 4;
      addTextWithWrap(courrier.procede_fabrication || '[Non fourni]');

      // --- SECTION 3 : Annexe 2 sur une NOUVELLE PAGE ---
      mainPdfDoc.addPage();
      yPosition = margin;
      addTextWithWrap("ANNEXE 2 : ATTESTATION SUR L'HONNEUR", { fontSize: 12, isBold: true });
      yPosition += 4;
      addTextWithWrap(courrier.attestation_honneur || '[Non fourni]');

      // Convertir le PDF de jsPDF pour pdf-lib (inchangé)
      const mainPdfBytes = mainPdfDoc.output('arraybuffer');
      const finalPdf = await PDFDocument.load(mainPdfBytes);

      // Le reste de la logique pour fusionner les PDFs et images avec pdf-lib est correct et reste inchangé
      for (const pdfAtt of pdfAttachments) {
        try {
          const pdfBytes = await fetch(pdfAtt.url).then(res => res.arrayBuffer());
          const donorPdf = await PDFDocument.load(pdfBytes);
          const copiedPages = await finalPdf.copyPages(donorPdf, donorPdf.getPageIndices());
          copiedPages.forEach(page => finalPdf.addPage(page));
        } catch (e) { console.error(`Erreur fusion PDF ${pdfAtt.name}:`, e); }
      }

      for (const imageAtt of imageAttachments) {
        try {
          const imageBytes = await fetch(imageAtt.url).then(res => res.arrayBuffer());
          const embeddedImage = imageAtt.name.endsWith('.png') ? await finalPdf.embedPng(imageBytes) : await finalPdf.embedJpg(imageBytes);
          const page = finalPdf.addPage();
          page.drawText(`Annexe : ${imageAtt.name}`, { x: margin, y: page.getHeight() - margin, size: 12 });
          const dims = embeddedImage.scaleToFit(page.getWidth() - 2 * margin, page.getHeight() - 2 * margin - 20);
          page.drawImage(embeddedImage, { x: (page.getWidth() - dims.width) / 2, y: (page.getHeight() - dims.height) / 2, width: dims.width, height: dims.height });
        } catch(e) { console.error(`Erreur image ${imageAtt.name}:`, e); }
      }

      // Sauvegarder le PDF final (inchangé)
      const finalPdfBytes = await finalPdf.save();
      const blob = new Blob([finalPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `courrier_assimilation_complet_${courrier.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert('Erreur lors de la génération du PDF complet.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Gestion des changements de champs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Gestion des cases à cocher PJ
  const handlePJCheck = (pjId, checked) => {
    if (pjId === 'attestation') return; 
    setFormData(prev => ({
      ...prev,
      piecesJointes: {
        ...prev.piecesJointes,
        [pjId]: { ...prev.piecesJointes[pjId], checked }
      }
    }));
  };

  // Gestion de l'upload de fichiers
  const handleFileChange = (pjId, files) => {
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setFormData(prev => ({
        ...prev,
        piecesJointes: {
          ...prev.piecesJointes,
          [pjId]: {
            ...prev.piecesJointes[pjId],
            files: [...prev.piecesJointes[pjId].files, ...fileArray]
          }
        }
      }));
    }
  };

  // Suppression d'un fichier
  const handleFileRemove = (pjId, fileIndex) => {
    setFormData(prev => ({
      ...prev,
      piecesJointes: {
        ...prev.piecesJointes,
        [pjId]: {
          ...prev.piecesJointes[pjId],
          files: prev.piecesJointes[pjId].files.filter((_, index) => index !== fileIndex)
        }
      }
    }));
  };

  // Gestion description "Autres PJ"
  const handleAutresPJDescription = (description) => {
    setFormData(prev => ({
      ...prev,
      piecesJointes: {
        ...prev.piecesJointes,
        autres: { ...prev.piecesJointes.autres, description }
      }
    }));
  };

  // Validation des PJ
  const validatePJ = () => {
    const errors = [];
    Object.entries(formData.piecesJointes).forEach(([pjId, pjData]) => {
      if (pjData.checked && pjData.files.length === 0 && pjId !== 'attestation') {
        const pjLabel = availablePJ.find(pj => pj.id === pjId)?.label || pjId;
        errors.push(`Veuillez ajouter des fichiers pour : ${pjLabel}`);
      }
    });
    return errors;
  };

  // Génération de la liste des justificatifs
  const generateJustificatifs = () => {
    const justificatifs = [];
    Object.entries(formData.piecesJointes).forEach(([pjId, pjData]) => {
      if (pjData.checked) {
        const pjInfo = availablePJ.find(pj => pj.id === pjId);
        if (pjInfo) {
          if (pjId === 'autres' && pjData.description) {
            justificatifs.push(pjData.description);
          } else {
            justificatifs.push(pjInfo.label);
          }
        }
      }
    });
    return justificatifs;
  };

  // Prévisualisation du courrier
  const generateCourrierPreview = (courrierData = null) => {
    const data = courrierData || formData;
    const aujourdhui = new Date().toLocaleDateString('fr-FR');
    const justificatifs = courrierData ? 
  (courrierData.pieces_jointes ? Object.entries(courrierData.pieces_jointes)
    .filter(([_, pjData]) => pjData.checked)
    .map(([pjId, pjData]) => {
      const pjInfo = availablePJ.find(pj => pj.id === pjId);
      if (pjId === 'autres' && pjData.description) return pjData.description;
      if (pjInfo) {
        // AJOUTER le nombre de fichiers
        const fileCount = pjData.files ? pjData.files.length : 0;
        return `${pjInfo.label}${fileCount > 0 ? ` (${fileCount} fichier${fileCount > 1 ? 's' : ''})` : ''}`;
      }
      return pjId;
    }) : []) : 
  generateJustificatifs();
    
    // ✅ CORRECTION : On structure le retour pour séparer le corps des annexes
    const mainBody = `${clientData?.nom_entreprise || '[Nom de l\'entreprise]'}
${clientData?.adresse_siege || '[Adresse]'}
SIRET : ${clientData?.siret || '[SIRET]'}

À l'attention du ${data.sie_nom || data.sieNom || '[Nom du SIE]'}
${data.sie_adresse || data.sieAdresse || '[Adresse du SIE]'}

${data.sie_ville || data.sieVille || '[Ville]'}, le ${aujourdhui}

Objet : ${data.objet_courrier || data.objetCourrier || '[Objet du courrier]'}
PJ : ${justificatifs.length > 0 ? justificatifs.join(', ') : '[Pièces jointes]'}

Madame, Monsieur,

${data.premier_paragraphe || data.premierParagraphe || '[Premier paragraphe]'}

I. Activité de l'entreprise
${data.activite_entreprise || data.activiteEntreprise || '[Description de l\'activité]'}

II. Fondement légal et doctrinal
Conformément aux dispositions du BOFiP-Impôts – BOI-TAXE-10-20-40 (§ 70 et suivants), il est précisé que :

"L'éligibilité à la réduction/exonération de CSPE s'apprécie au regard de la réalité de l'activité exercée, indépendamment du code NAF/APE de l'entreprise, dès lors qu'une activité industrielle de transformation est effectivement réalisée et dûment justifiée."

De plus, selon le BOI-TAXE-10-20-40 § 80 :

"Une entreprise dont le code APE n'est pas a priori éligible peut bénéficier du dispositif dès lors que l'activité réellement exercée relève de la transformation de matière, caractéristique des activités industrielles…"

III. Justification de la demande
Nous joignons à la présente :

${justificatifs.map(just => `• ${just}`).join('\n')}

IV. Demande
En conséquence, nous sollicitons l'assimilation de notre activité à une activité industrielle éligible à la réduction/exonération de CSPE, conformément à la doctrine précitée, et ce, nonobstant notre code APE actuel.

Nous restons à votre disposition pour tout complément d'information ou contrôle sur place, et vous prions de bien vouloir nous confirmer la prise en compte de notre demande.

Nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.

${clientData?.dirigeant_nom || '[Nom du signataire]'}
[Coordonnées]`;

    const annexe1 = `---

ANNEXE 1 : DESCRIPTIF DU PROCÉDÉ DE FABRICATION

${data.procede_fabrication || data.procededFabrication || '[Descriptif détaillé du procédé]'}`;

    // ✅ CORRECTION : On crée une section dédiée pour l'attestation
    const annexe2 = `---

ANNEXE 2 : ATTESTATION SUR L'HONNEUR

${data.attestation_honneur || data.attestationHonneur || '[Attestation sur l\'honneur]'}`;

    // On retourne l'ensemble
    return `${mainBody}\n\n${annexe1}\n\n${annexe2}`;
     };

  // Prévisualisation
  const handlePreview = () => {
    const errors = validatePJ();
    if (errors.length > 0) {
      alert('Erreurs de validation :\n' + errors.join('\n'));
      return;
    }
    setShowPreview(true);
  };

  // Sauvegarde
  const handleSave = async () => {
    setIsGenerating(true);
    try {
      // Validation (inchangée)
      const errors = validatePJ();
      if (errors.length > 0) {
        alert('Erreurs de validation :\n' + errors.join('\n'));
        setIsGenerating(false);
        return;
      }

      // --- 1. UPLOAD DES PIÈCES JOINTES (votre logique est déjà bonne) ---
      const uploadedPiecesJointes = { ...formData.piecesJointes };
      for (const pjId in uploadedPiecesJointes) {
        if (uploadedPiecesJointes[pjId].checked && uploadedPiecesJointes[pjId].files.length > 0) {
          const filePaths = [];
          for (const file of uploadedPiecesJointes[pjId].files) {
            if (typeof file !== 'string') {
              const filePath = `${clientId}/${pjId}/${Date.now()}-${file.name}`;
              const { error: uploadError } = await supabase.storage.from('pieces-jointes-assimilation').upload(filePath, file);
              if (uploadError) throw new Error(`Erreur upload PJ: ${uploadError.message}`);
              filePaths.push(filePath);
            } else {
              filePaths.push(file);
            }
          }
          uploadedPiecesJointes[pjId].files = filePaths;
        }
      }

      // --- 2. PRÉPARATION DES DONNÉES POUR LA BDD ---
      const courrierDataToSave = {
        client_id: clientId,
        sie_nom: formData.sieNom,
        sie_adresse: formData.sieAdresse,
        sie_ville: formData.sieVille,
        objet_courrier: formData.objetCourrier,
        premier_paragraphe: formData.premierParagraphe,
        activite_entreprise: formData.activiteEntreprise,
        attestation_honneur: formData.attestationHonneur,
        procede_fabrication: formData.procededFabrication,
        pieces_jointes: uploadedPiecesJointes,
        status: 'completed',
        updated_at: new Date().toISOString()
      };
      
      if (selectedCourrier && view === 'edit') {
        courrierDataToSave.id = selectedCourrier.id;
      } else {
        courrierDataToSave.created_at = new Date().toISOString();
      }

      // --- 3. GÉNÉRATION DU PDF FINAL (logique complète) ---
      const { jsPDF } = await import('jspdf');
      const { PDFDocument } = await import('pdf-lib');
      
      const mainPdfDoc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = mainPdfDoc.internal.pageSize.getWidth();
      const pageHeight = mainPdfDoc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;

      const addTextWithWrap = (text, options = {}) => {
        const { fontSize = 10, isBold = false, x = margin, align = 'left' } = options;
        mainPdfDoc.setFontSize(fontSize);
        mainPdfDoc.setFont(undefined, isBold ? 'bold' : 'normal');
        const lines = mainPdfDoc.splitTextToSize(text, pageWidth - (2 * margin));
        lines.forEach(line => {
          if (yPosition > pageHeight - margin) {
            mainPdfDoc.addPage();
            yPosition = margin;
          }
          mainPdfDoc.text(line, x, yPosition, { align: align });
          yPosition += 6;
        });
        yPosition += 2;
      };

      const courrierBody = generateCourrierPreview(courrierDataToSave).split('---')[0];
      addTextWithWrap("COURRIER D'ASSIMILATION", { fontSize: 16, isBold: true, x: pageWidth / 2, align: 'center' });
      yPosition += 10;
      addTextWithWrap(courrierBody.trim());
      mainPdfDoc.addPage(); yPosition = margin;
      addTextWithWrap("ANNEXE 1 : DESCRIPTIF DU PROCÉDÉ DE FABRICATION", { fontSize: 12, isBold: true }); yPosition += 4;
      addTextWithWrap(courrierDataToSave.procede_fabrication || '[Non fourni]');
      mainPdfDoc.addPage(); yPosition = margin;
      addTextWithWrap("ANNEXE 2 : ATTESTATION SUR L'HONNEUR", { fontSize: 12, isBold: true }); yPosition += 4;
      addTextWithWrap(courrierDataToSave.attestation_honneur || '[Non fourni]');
      
      const mainPdfBytes = mainPdfDoc.output('arraybuffer');
      const finalPdf = await PDFDocument.load(mainPdfBytes);

      // ✅ --- LOGIQUE DE FUSION DES ANNEXES COMPLÈTE ---
      // On se base sur les `uploadedPiecesJointes` qui contiennent les chemins des fichiers
      const imageAttachments = [];
      const pdfAttachments = [];

      for (const [pjId, pjData] of Object.entries(uploadedPiecesJointes)) {
          if (pjData.checked && pjData.files && pjData.files.length > 0) {
              for (const filePath of pjData.files) {
                  const fileExtension = filePath.split('.').pop().toLowerCase();
                  const attachmentData = { name: filePath.split('/').pop(), path: filePath };

                  if (['jpg', 'jpeg', 'png'].includes(fileExtension)) {
                      imageAttachments.push(attachmentData);
                  } else if (fileExtension === 'pdf') {
                      pdfAttachments.push(attachmentData);
                  }
              }
          }
      }
      
      // Fusion des PDFs
      for (const pdfAtt of pdfAttachments) {
          try {
              const { data: fileBlob, error } = await supabase.storage.from('pieces-jointes-assimilation').download(pdfAtt.path);
              if (error) throw error;
              const pdfBytes = await fileBlob.arrayBuffer();
              const donorPdf = await PDFDocument.load(pdfBytes);
              const copiedPages = await finalPdf.copyPages(donorPdf, donorPdf.getPageIndices());
              copiedPages.forEach(page => finalPdf.addPage(page));
          } catch (e) { console.error(`Erreur fusion PDF ${pdfAtt.name}:`, e); }
      }

      // Intégration des Images
      for (const imageAtt of imageAttachments) {
          try {
              const { data: fileBlob, error } = await supabase.storage.from('pieces-jointes-assimilation').download(imageAtt.path);
              if (error) throw error;
              const imageBytes = await fileBlob.arrayBuffer();
              const embeddedImage = imageAtt.name.endsWith('.png') ? await finalPdf.embedPng(imageBytes) : await finalPdf.embedJpg(imageBytes);
              const page = finalPdf.addPage();
              page.drawText(`Annexe : ${imageAtt.name}`, { x: margin, y: page.getHeight() - margin, size: 12 });
              const dims = embeddedImage.scaleToFit(page.getWidth() - 2 * margin, page.getHeight() - 2 * margin - 20);
              page.drawImage(embeddedImage, { x: (page.getWidth() - dims.width) / 2, y: (page.getHeight() - dims.height) / 2, width: dims.width, height: dims.height });
          } catch(e) { console.error(`Erreur image ${imageAtt.name}:`, e); }
      }
      // --- FIN DE LA LOGIQUE DE FUSION ---

      const finalPdfBytes = await finalPdf.save();

      // --- 4. UPLOAD DU PDF FINAL ---
      const pdfFileName = `Courrier-Assimilation-${clientData.nom_entreprise.replace(/\s/g, '_')}-${courrierDataToSave.id || Date.now()}.pdf`;
      const pdfStoragePath = `${clientId}/${pdfFileName}`;
      
      const { error: pdfUploadError } = await supabase.storage
        .from('courriers-assimilation-final') // Assurez-vous d'avoir ce bucket
        .upload(pdfStoragePath, finalPdfBytes, { contentType: 'application/pdf', upsert: true });

      if (pdfUploadError) throw new Error(`Erreur upload PDF final: ${pdfUploadError.message}`);

      // On ajoute le chemin du PDF final aux données à sauvegarder
      courrierDataToSave.final_letter_url = pdfStoragePath;

      // --- 5. SAUVEGARDE FINALE EN BDD ---
      const { error } = await supabase.from('assimilations').upsert(courrierDataToSave);
      if (error) throw error;
      
      alert('Courrier sauvegardé et PDF final généré avec succès !');
      handleBackToDashboard();

    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde : ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <FiLoader className="animate-spin h-8 w-8 mx-auto text-blue-500" />
        <p className="mt-2 text-slate-600">Chargement des données...</p>
      </div>
    );
  }

  // Rendu conditionnel selon la vue
  if (view === 'dashboard') {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto p-4 md:p-6">
          <AssimilationDashboard
            courriers={courriers}
            onCreateNew={handleCreateNew}
            onViewCourrier={handleViewCourrier}
            onEditCourrier={handleEditCourrier}
            onDeleteCourrier={handleDeleteCourrier}
            onDownloadCourrier={handleDownloadCourrier}
            clientData={clientData}
          />
        </div>
      </div>
    );
  }

  if (view === 'view') {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto p-4 md:p-6">
          <CourrierViewer
            courrier={selectedCourrier}
            onBack={handleBackToDashboard}
            onEdit={handleEditCourrier}
            onDownload={handleDownloadCourrier}
            clientData={clientData}
          />
        </div>
      </div>
    );
  }

  // Vue création/édition (formulaire)
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {/* En-tête avec bouton retour */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={handleBackToDashboard}
                className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors mr-3"
              >
                <FiArrowLeft className="h-5 w-5" />
              </button>
              <h2 className="text-2xl font-bold text-slate-900">
                <FiFileText className="inline-block h-6 w-6 mr-3 text-blue-600" />
                {view === 'edit' ? 'Modifier le courrier d\'assimilation' : 'Créer un nouveau courrier d\'assimilation'}
              </h2>
            </div>
          </div>
          
          {/* Informations Client */}
          {clientData && (
            <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Informations Client</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-white p-3 rounded border">
                  <strong className="text-slate-700">Entreprise :</strong> 
                  <span className="ml-2 text-slate-900">{clientData.nom_entreprise}</span>
                </div>
                <div className="bg-white p-3 rounded border">
                  <strong className="text-slate-700">SIRET :</strong> 
                  <span className="ml-2 text-slate-900">{clientData.siret}</span>
                </div>
                <div className="bg-white p-3 rounded border">
                  <strong className="text-slate-700">Adresse :</strong> 
                  <span className="ml-2 text-slate-900">{clientData.adresse_siege}</span>
                </div>
                <div className="bg-white p-3 rounded border">
                  <strong className="text-slate-700">Dirigeant :</strong> 
                  <span className="ml-2 text-slate-900">{clientData.dirigeant_nom}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Informations SIE */}
        <FormSection title="Informations du SIE" icon={<FiFileText className="h-5 w-5 mr-3 text-green-600" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nom du SIE
              </label>
              <input
                type="text"
                name="sieNom"
                value={formData.sieNom}
                onChange={handleInputChange}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Service des Impôts des Entreprises de [Ville]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Ville du SIE
              </label>
              <input
                type="text"
                name="sieVille"
                value={formData.sieVille}
                onChange={handleInputChange}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Paris"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Adresse complète du SIE
            </label>
            <textarea
              name="sieAdresse"
              value={formData.sieAdresse}
              onChange={handleInputChange}
              rows="3"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="[Adresse complète du SIE]&#10;[Code Postal] [Ville]"
            />
          </div>
        </FormSection>

        {/* Contenu du courrier */}
        <FormSection title="Contenu du courrier" icon={<FiFileText className="h-5 w-5 mr-3 text-purple-600" />}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Objet du courrier
              </label>
              <input
                type="text"
                name="objetCourrier"
                value={formData.objetCourrier}
                onChange={handleInputChange}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Demande d'assimilation de l'activité..."
              />
              <p className="text-xs text-slate-500 mt-1 bg-slate-50 p-2 rounded">
                <strong>Exemple :</strong> Demande d'assimilation de l'activité de fabrication artisanale de pâtes à une activité industrielle éligible à la réduction/exonération de CSPE
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Premier paragraphe
              </label>
              <textarea
                name="premierParagraphe"
                value={formData.premierParagraphe}
                onChange={handleInputChange}
                rows="4"
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Nous sollicitons, par la présente, l'assimilation de notre activité..."
              />
              <p className="text-xs text-slate-500 mt-1 bg-slate-50 p-2 rounded">
                <strong>Exemple :</strong> Nous sollicitons, par la présente, l'assimilation de notre activité de fabrication artisanale de pâtes fraîches à une activité industrielle au sens des articles 266 quinquies C et suivants du Code des douanes et de l'article 238 du Code général des impôts (CGI), dans le cadre de notre demande de réduction/exonération de Contribution au Service Public de l'Électricité (CSPE).
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Section I. Activité de l'entreprise
              </label>
              <textarea
                name="activiteEntreprise"
                value={formData.activiteEntreprise}
                onChange={handleInputChange}
                rows="6"
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Description de l'activité de l'entreprise..."
              />
              <p className="text-xs text-slate-500 mt-1 bg-slate-50 p-2 rounded">
                <strong>Exemple :</strong> Notre entreprise, actuellement enregistrée sous le code APE 47.29Z – Commerce de détail alimentaire sur éventaires et marchés, exerce principalement une activité de transformation de matières premières (semoule de blé, œufs, eau, etc.) en pâtes fraîches artisanales destinées à la vente directe ou à la distribution auprès de professionnels. Cette activité correspond, au sens de la nomenclature INSEE, au code 10.13A – Fabrication de pâtes alimentaires, reconnu comme activité industrielle.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Attestation sur l'honneur <span className="text-red-500">*</span>
              </label>
              <textarea
                name="attestationHonneur"
                value={formData.attestationHonneur}
                onChange={handleInputChange}
                rows="4"
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Je soussigné(e), atteste que..."
              />
              <p className="text-xs text-slate-500 mt-1 bg-slate-50 p-2 rounded">
                <strong>Exemple :</strong> Je soussigné(e), [Nom du dirigeant], agissant en qualité de [Qualité] de la société [Nom de l'entreprise], atteste sur l'honneur que la fabrication de [produits] constitue l'activité principale de l'entreprise, représentant plus de 50% du chiffre d'affaires et du temps de travail annuel. Cette activité de transformation s'effectue de manière régulière et permanente dans nos locaux situés à [adresse].
              </p>
            </div>
          </div>
        </FormSection>

        {/* Pièces jointes */}
        <FormSection title="Pièces jointes" icon={<FiPaperclip className="h-5 w-5 mr-3 text-orange-600" />}>
          <div className="space-y-4">
            {availablePJ.map((pj) => (
              <div key={pj.id} className="space-y-2">
                <div className="flex items-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <input
                    type="checkbox"
                    id={`pj-${pj.id}`}
                    checked={formData.piecesJointes[pj.id]?.checked || false}
                    onChange={(e) => handlePJCheck(pj.id, e.target.checked)}
                    disabled={pj.id === 'attestation'} 
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:bg-slate-200 disabled:cursor-not-allowed"
                  />
                  <label htmlFor={`pj-${pj.id}`} className="ml-3 text-sm font-medium text-slate-700 flex-1">
                    {pj.label}
                  </label>
                  {formData.piecesJointes[pj.id]?.checked && (
                    <FiCheck className="h-5 w-5 text-green-500" />
                  )}
                </div>

                {/* Zone pour "Autres" avec description */}
                {pj.id === 'autres' && formData.piecesJointes[pj.id]?.checked && (
                  <div className="ml-6">
                    <input
                      type="text"
                      placeholder="Précisez le type de justificatif"
                      value={formData.piecesJointes.autres?.description || ''}
                      onChange={(e) => handleAutresPJDescription(e.target.value)}
                      className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                )}

                {/* Zone d'upload */}
                {pj.id !== 'attestation' && (
                  <PJUploadSection
                    pjId={pj.id}
                    files={formData.piecesJointes[pj.id]?.files || []}
                    onFileChange={handleFileChange}
                    onFileRemove={handleFileRemove}
                    isChecked={formData.piecesJointes[pj.id]?.checked || false}
                  />
                )}
              </div>
            ))}
          </div>
        </FormSection>

        {/* Annexe 1 */}
        <FormSection title="Annexe 1 : Descriptif du procédé de fabrication" icon={<FiFileText className="h-5 w-5 mr-3 text-red-600" />}>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Descriptif détaillé <span className="text-red-500">*</span>
            </label>
            <textarea
              name="procededFabrication"
              value={formData.procededFabrication}
              onChange={handleInputChange}
              rows="8"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Décrivez ici chaque étape de la transformation des matières premières en produit fini..."
            />
          </div>
        </FormSection>

        {/* Boutons d'action */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <button
              onClick={handleBackToDashboard}
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <FiArrowLeft className="h-5 w-5 mr-2" />
              Retour à la liste
            </button>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handlePreview}
                disabled={isGenerating}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                <FiEye className="h-5 w-5 mr-2" />
                Prévisualiser le courrier
              </button>
              
              <button
                onClick={handleSave}
                disabled={isGenerating}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                {isGenerating ? (
                  <FiLoader className="animate-spin h-5 w-5 mr-2" />
                ) : (
                  <FiSave className="h-5 w-5 mr-2" />
                )}
                {view === 'edit' ? 'Mettre à jour' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>

        {/* Modal de prévisualisation */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl max-h-[90vh] overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6 border-b border-slate-200 pb-4">
                  <h3 className="text-xl font-semibold text-slate-900">Prévisualisation du courrier</h3>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <FiX className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm font-mono text-slate-800 leading-relaxed">
                    {generateCourrierPreview()}
                  </pre>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    Fermer
                  </button>
                  <button
                    onClick={() => {
                      setShowPreview(false);
                      handleSave();
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Sauvegarder
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssimilationTabContent;